import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
// import 'bootstrap/dist/css/bootstrap.min.css'; // Ensure Bootstrap is included

import axios from "axios";
import { BASE_URL, IMAGE_URL, security_key } from "../../../global";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";

import AddAd from "./AddAd";
import EditAd from "./EditAd";

const AdManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [ads, setAds] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAd, setEditAd] = useState(null); // Separate state for editing

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  
  const filteredAds = useMemo(() => {
    return ads.filter((ad) =>
      ad.adv_title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [ads, searchTerm]);

  const paginatedAds = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredAds.slice(start, end);
  }, [filteredAds, currentPage]);

  const totalPages = Math.ceil(filteredAds.length / ITEMS_PER_PAGE);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };


  useEffect(() => {
    fetchAllAds();
  }, []);

  const fetchAllAds = async () => {
    try {
      const response = await axios.post(BASE_URL + "API/apicall", {
        security_key: security_key,
        mode: "getAllAdvertisement",
      });

      const output = response.data;

      setAds(output);
    } catch (error) {
      console.error("An error occurred: ", error);
      setAds([]);
    }
  };

  const handleEdit = (ad) => {
    setEditAd(ad);
    setShowEditModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleCloseEditModal = () => {
    setEditAd(null);
    setShowEditModal(false);
  };

  const handleDelete = async (adv_key) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it",
      });

      if (result.isConfirmed) {
        const response = await axios.post(BASE_URL + "API/apicall", {
          security_key: security_key,
          mode: "deleteAdvertisement",
          pk_adv_key: adv_key,
        });

        const output = response.data;

        if (output == 1) {
          await Swal.fire(
            "Deleted!",
            "The advertisement has been successfully removed.",
            "success"
          );
          fetchAllAds();
        }
      }
    } catch (error) {
      console.error("An error occurred: ", error);
    }
  };

  return (
    <div className="container mb-3">
      <ToastContainer position="top-center" />
      <div className="d-flex justify-content-between align-items-center admin-page-head">
        <h4>Advertisement Management</h4>
        <Button
          variant="dark"
          onClick={() => setShowAddModal(true)}
          className="mb-3"
        >
          Add New Ad
        </Button>
      </div>

      <div className="mb-4">
        <Form>
          <div className="row">
            <div className="col-md-4">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Search by name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        </Form>
      </div>

      <div className="row">
        <div className="col-12 table-responsive">
          <table className="table table-striped table-bordered table-responsive-scroll">
            <thead className="thead-dark">
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Category</th>
                <th>Description</th>
                <th>Banner</th>
                <th>Position</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAds.length && filteredAds.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <h6 className="text-center">Data not available</h6>
                  </td>
                </tr>
              ) : (
                paginatedAds.map((ad, index) => (
                  <tr key={ad.pk_adv_key}>
                    <td>{index + 1}</td>
                    <td>{ad.adv_title}</td>
                    <td>{ad.cat_name}</td>
                    <td>{ad.adv_description}</td>
                    <td>
                      {ad.adv_img_mod && (
                        <img
                          src={IMAGE_URL + ad.adv_img_mod}
                          alt="Ad"
                          className="img-thumbnail"
                          style={{ maxWidth: "100px" }}
                        />
                      )}
                    </td>
                    <td>{ad.adposition}</td>
                    <td>
                      <div className="actbtn-grp h-100 flex-wrap flex-md-nowrap align-items-center">
                        <Button
                          variant="outline-dark"
                          size="sm"
                          onClick={() => handleEdit(ad)}
                          data-bs-toggle="tooltip"
                          title="Edit"
                        >
                          <i className="fa-solid fa-pen-to-square fa-lg"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(ad.pk_adv_key)}
                          data-bs-toggle="tooltip"
                          title="Delete"
                        >
                          <i className="fa-solid fa-trash fa-lg"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination d-flex justify-content-end mb-3 my-3">
          <Button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="btn btn-light mx-1"
          >
            &lt;&lt;
          </Button>
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-light mx-1"
          >
            &lt;
          </Button>
          {Array.from({ length: totalPages }, (_, index) => (
            <Button
              key={index + 1}
              onClick={() => handlePageChange(index + 1)}
              className={`mx-1 ${
                currentPage === index + 1 ? "btn-dark text-white" : "btn-light"
              }`}
            >
              {index + 1}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-light mx-1"
          >
            &gt;
          </Button>
          <Button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="btn btn-light mx-1"
          >
            &gt;&gt;
          </Button>
        </div>
        <AddAd
          showModal={showAddModal}
          handleClose={handleCloseAddModal}
          fetchAllAds={fetchAllAds}
        />
        <EditAd
          showModal={showEditModal}
          handleClose={handleCloseEditModal}
          fetchAllAds={fetchAllAds}
          ad={editAd}
        />
      </div>

      <style jsx>{`
        .actbtn-grp {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
      `}</style>
    </div>
  );
};

export default AdManagement;
